import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { useNavigate, useParams } from 'react-router-dom';
import { getARInvoice, createARInvoice, updateARInvoice, getCustomersList, getCostCenters, getAccounts, getProjectsList, getCompanies, getCurrencies, getPaymentTypes, getBankAccounts } from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, DocumentArrowUpIcon, XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import SearchableSelect from '../components/SearchableSelect';

const InvoiceForm = () => {
  usePageTitle('Invoice Form');

  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // Helper function to format date for input fields
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingFileLink, setExistingFileLink] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    company_id: '',
    reference_number: '',
    description: '',
    currency: 'EUR',
    issue_date: new Date().toISOString().split('T')[0],
    document_date: '',
    due_date: '',
    pnl_period: '',
    include_in_pnl: false,
    payment_status: 'not_received',
    payment_type: 'bank_transfer',
    bank_account: '',
    paid_date: '',
  });

  const [lineItems, setLineItems] = useState([
    { description: '', amount: '', cost_center_id: '', account_id: '', project_id: '', is_vat_line: false, vat_deductible: false }
  ]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [customersRes, companiesRes, costCentersRes, accountsRes, projectsRes, currenciesRes, paymentTypesRes, bankAccountsRes] = await Promise.all([
          getCustomersList(), getCompanies(), getCostCenters(), getAccounts(), getProjectsList(), getCurrencies(), getPaymentTypes(), getBankAccounts()
        ]);
        setCustomers(customersRes.data);
        setCompanies(companiesRes.data);
        setCostCenters(costCentersRes.data);
        setAccounts(accountsRes.data);
        setProjects(projectsRes.data);
        setCurrencies(currenciesRes.data);
        setPaymentTypes(paymentTypesRes.data);
        setBankAccounts(bankAccountsRes.data);
      } catch (error) {
        console.error('Error loading lookups:', error);
      }
    };
    loadLookups();
  }, []);

  useEffect(() => {
    if (isEdit) {
      const loadInvoice = async () => {
        try {
          const response = await getARInvoice(id);
          const inv = response.data.invoice;

          // Prevent editing rejected invoices
          if (inv.approval_status === 'rejected') {
            toast.error('Rejected invoices cannot be edited');
            navigate(`/ar-invoices/${id}`);
            return;
          }

          setFormData({
            customer_id: inv.customer_id || '',
            company_id: inv.company_id || '',
            reference_number: inv.reference_number || '',
            description: inv.description || '',
            currency: inv.currency || 'EUR',
            issue_date: formatDateForInput(inv.issue_date),
            document_date: formatDateForInput(inv.document_date),
            due_date: formatDateForInput(inv.due_date),
            pnl_period: formatDateForInput(inv.pnl_period),
            include_in_pnl: inv.include_in_pnl || false,
            payment_status: inv.payment_status || 'not_received',
            payment_type: inv.payment_type || 'bank_transfer',
            bank_account: inv.bank_account || '',
            paid_date: formatDateForInput(inv.paid_date),
          });
          if (response.data.lineItems && response.data.lineItems.length > 0) {
            setLineItems(response.data.lineItems.map(li => ({
              description: li.description || '',
              amount: li.amount || '',
              cost_center_id: li.cost_center_id || '',
              account_id: li.account_id || '',
              project_id: li.project_id || '',
              is_vat_line: Boolean(li.is_vat_line),
              vat_deductible: Boolean(li.vat_deductible)
            })));
          }
          setExistingFileLink(inv.google_drive_link);
        } catch (error) {
          toast.error('Error loading invoice');
          navigate('/ar-invoices');
        } finally {
          setLoading(false);
        }
      };
      loadInvoice();
    }
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;
    
    // When cost center changes, check if it's VAT
    if (field === 'cost_center_id') {
      const cc = costCenters.find(c => c.id === parseInt(value));
      newItems[index].is_vat_line = cc?.is_vat ? true : false;
      // Reset vat_deductible if not a VAT line
      if (!cc?.is_vat) {
        newItems[index].vat_deductible = false;
      }
    }
    
    setLineItems(newItems);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', amount: '', cost_center_id: '', account_id: '', is_vat_line: false, vat_deductible: false }]);
  };

  const addVatLine = () => {
    const vatCostCenter = costCenters.find(cc => cc.is_vat);
    setLineItems([...lineItems, { 
      description: 'VAT', 
      amount: '', 
      cost_center_id: vatCostCenter?.id || '', 
      account_id: '', 
      is_vat_line: true, 
      vat_deductible: true 
    }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const regularTotal = lineItems.filter(i => !i.is_vat_line).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const vatTotal = lineItems.filter(i => i.is_vat_line).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    return { regularTotal, vatTotal, grandTotal: regularTotal + vatTotal };
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') { toast.error('Only PDF files allowed'); return; }
      if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.issue_date || !formData.document_date || !formData.due_date) {
      toast.error('Please fill required fields');
      return;
    }
    if (lineItems.length === 0 || !lineItems.some(li => li.amount)) {
      toast.error('Add at least one line item with amount');
      return;
    }

    setSaving(true);
    try {
      const data = { ...formData, line_items: lineItems };
      if (selectedFile) data.invoice_file = selectedFile;

      if (isEdit) {
        await updateARInvoice(id, data);
        toast.success('Invoice updated');
      } else {
        await createARInvoice(data);
        toast.success('Invoice created');
      }
      navigate('/ar-invoices');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: formData.currency || 'EUR' }).format(amount || 0);
  const totals = calculateTotals();
  
  const isVatCostCenter = (costCenterId) => {
    const cc = costCenters.find(c => c.id === parseInt(costCenterId));
    return cc?.is_vat;
  };

  const hasVatLine = lineItems.some(li => li.is_vat_line);

  if (loading) return <div className="loading-overlay"><div className="spinner" /><span>Loading...</span></div>;

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-md">
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/ar-invoices')}><ArrowLeftIcon style={{ width: 20, height: 20 }} /></button>
          <div>
            <h1 className="page-title">{isEdit ? 'Edit Invoice' : 'New Invoice'}</h1>
            <p className="page-subtitle">{isEdit ? 'Update invoice details' : 'Create a new AP invoice'}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
          <h3 className="card-title" style={{ marginBottom: 'var(--space-lg)' }}>Basic Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Customer *</label>
              <select name="customer_id" className="form-select" value={formData.customer_id} onChange={handleChange} required>
                <option value="">Select vendor</option>
                {customers.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Company</label>
              <select name="company_id" className="form-select" value={formData.company_id} onChange={handleChange}>
                <option value="">Select company</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Reference Number</label>
              <input type="text" name="reference_number" className="form-input" value={formData.reference_number} onChange={handleChange} placeholder="Customer's invoice #" />
            </div>
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select name="currency" className="form-select" value={formData.currency} onChange={handleChange}>
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea name="description" className="form-textarea" value={formData.description} onChange={handleChange} placeholder="Invoice description" rows={2} />
          </div>
        </div>

        {/* Dates */}
        <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
          <h3 className="card-title" style={{ marginBottom: 'var(--space-lg)' }}>Dates</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Issue Date *</label>
              <input type="date" name="issue_date" className="form-input" value={formData.issue_date} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Document Date *</label>
              <input type="date" name="document_date" className="form-input" value={formData.document_date} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Due Date *</label>
              <input type="date" name="due_date" className="form-input" value={formData.due_date} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">P&L Period</h3>
            <div className="form-row">
              <div className="form-group" style={{ flex: '0 0 calc(33.333% - var(--space-md) / 3)' }}>
                <label className="form-label">P&L Period</label>
                <input
                  type="month"
                  name="pnl_period"
                  className="form-input"
                  value={formData.pnl_period ? formData.pnl_period.substring(0, 7) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, pnl_period: value ? `${value}-01` : '' }));
                  }}
                />
                <small className="form-help">Select month and year for P&L reporting</small>
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '1.5rem', flex: 1 }}>
                <input
                  type="checkbox"
                  name="include_in_pnl"
                  id="include_in_pnl_ar"
                  className="form-checkbox"
                  checked={formData.include_in_pnl}
                  onChange={(e) => setFormData(prev => ({ ...prev, include_in_pnl: e.target.checked }))}
                />
                <label htmlFor="include_in_pnl_ar" className="form-label" style={{ marginLeft: '0.5rem', marginBottom: 0 }}>
                  Include in P&L
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
          <div className="flex justify-between items-center" style={{ marginBottom: 'var(--space-lg)' }}>
            <h3 className="card-title" style={{ margin: 0 }}>Line Items</h3>
            <div className="flex gap-sm">
              <button type="button" className="btn btn-secondary btn-sm" onClick={addLineItem}>
                <PlusIcon style={{ width: 16, height: 16 }} /> Add Line
              </button>
              {!hasVatLine && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={addVatLine} style={{ background: 'var(--color-accent-warning)', borderColor: 'var(--color-accent-warning)' }}>
                  <PlusIcon style={{ width: 16, height: 16 }} /> Add VAT
                </button>
              )}
            </div>
          </div>
          
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Description</th>
                  <th style={{ width: '12%' }}>Amount</th>
                  <th style={{ width: '18%' }}>Cost Center</th>
                  <th style={{ width: '18%' }}>Account</th>
                  <th style={{ width: '18%' }}>Project</th>
                  <th style={{ width: '7%' }}>VAT Ded.</th>
                  <th style={{ width: '2%' }}></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, index) => (
                  <tr key={index} style={item.is_vat_line ? { background: 'rgba(245, 158, 11, 0.1)' } : {}}>
                    <td>
                      <input type="text" className="form-input" value={item.description} onChange={(e) => handleLineItemChange(index, 'description', e.target.value)} placeholder={item.is_vat_line ? 'VAT' : 'Description'} />
                    </td>
                    <td>
                      <input type="number" className="form-input" value={item.amount} onChange={(e) => handleLineItemChange(index, 'amount', e.target.value)} placeholder="0.00" step="0.01" />
                    </td>
                    <td>
                      <SearchableSelect
                        value={item.cost_center_id}
                        onChange={(value) => handleLineItemChange(index, 'cost_center_id', value)}
                        options={costCenters.map(cc => ({ value: cc.id, label: `${cc.code} - ${cc.name}` }))}
                        placeholder="Select cost center..."
                      />
                    </td>
                    <td>
                      <SearchableSelect
                        value={item.account_id}
                        onChange={(value) => handleLineItemChange(index, 'account_id', value)}
                        options={accounts.map(a => ({ value: a.id, label: `${a.account_number} - ${a.name}` }))}
                        placeholder="Select account..."
                      />
                    </td>
                    <td>
                      <select className="form-select" value={item.project_id} onChange={(e) => handleLineItemChange(index, 'project_id', e.target.value)}>
                        <option value="">Select</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.project_id} - {p.project_name}</option>)}
                      </select>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {isVatCostCenter(item.cost_center_id) && (
                        <input type="checkbox" checked={item.vat_deductible} onChange={(e) => handleLineItemChange(index, 'vat_deductible', e.target.checked)} title="VAT Deductible" />
                      )}
                    </td>
                    <td>
                      {lineItems.length > 1 && (
                        <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => removeLineItem(index)} style={{ color: 'var(--color-accent-danger)' }}>
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--color-bg-tertiary)' }}>
                  <td><strong>Subtotal (excl. VAT)</strong></td>
                  <td><strong>{formatCurrency(totals.regularTotal)}</strong></td>
                  <td colSpan="4"></td>
                </tr>
                {totals.vatTotal > 0 && (
                  <tr style={{ background: 'var(--color-bg-tertiary)' }}>
                    <td><strong>VAT</strong></td>
                    <td><strong>{formatCurrency(totals.vatTotal)}</strong></td>
                    <td colSpan="4"></td>
                  </tr>
                )}
                <tr style={{ background: 'var(--color-bg-tertiary)', fontWeight: 'bold' }}>
                  <td><strong>Total</strong></td>
                  <td style={{ fontSize: '1.1rem', color: 'var(--color-accent-primary)' }}><strong>{formatCurrency(totals.grandTotal)}</strong></td>
                  <td colSpan="4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Payment */}
        <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
          <h3 className="card-title" style={{ marginBottom: 'var(--space-lg)' }}>Payment</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Payment Status</label>
              <select name="payment_status" className="form-select" value={formData.payment_status} onChange={handleChange}>
                <option value="not_paid">Not Received</option>
                <option value="paid">Received</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Payment Type</label>
              <select name="payment_type" className="form-select" value={formData.payment_type} onChange={handleChange}>
                {paymentTypes.map(pt => <option key={pt.code} value={pt.code}>{pt.name}</option>)}
              </select>
            </div>
            {formData.payment_type === 'bank_transfer' && (
              <div className="form-group">
                <label className="form-label">Bank Account</label>
                <select name="bank_account" className="form-select" value={formData.bank_account} onChange={handleChange}>
                  <option value="">Select bank account</option>
                  {bankAccounts.map(ba => (
                    <option key={ba.id} value={ba.id}>
                      {ba.bank_name} - {ba.account_number || ba.iban || ba.name} ({ba.currency})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {formData.payment_status === 'received' && (
              <div className="form-group">
                <label className="form-label">Issue Date</label>
                <input type="date" name="paid_date" className="form-input" value={formData.paid_date} onChange={handleChange} />
              </div>
            )}
          </div>
        </div>

        {/* Document */}
        <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
          <h3 className="card-title" style={{ marginBottom: 'var(--space-lg)' }}>Invoice Document</h3>
          {existingFileLink && !selectedFile && (
            <div className="file-info" style={{ marginBottom: 'var(--space-md)' }}>
              <DocumentArrowUpIcon style={{ width: 24, height: 24, color: 'var(--color-accent-danger)' }} />
              <a href={existingFileLink} target="_blank" rel="noopener noreferrer" className="link">View current document</a>
            </div>
          )}
          <label className="file-upload">
            <input type="file" accept=".pdf" onChange={handleFileChange} />
            <DocumentArrowUpIcon className="file-upload-icon" />
            <p className="file-upload-text"><strong>Click to upload</strong> or drag and drop<br />PDF files only, max 10MB</p>
          </label>
          {selectedFile && (
            <div className="file-info">
              <DocumentArrowUpIcon style={{ width: 24, height: 24, color: 'var(--color-accent-danger)' }} />
              <span className="file-info-name">{selectedFile.name}</span>
              <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelectedFile(null)}><XMarkIcon style={{ width: 16, height: 16 }} /></button>
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/ar-invoices')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update Invoice' : 'Create Invoice'}</button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;
